import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Nav } from 'react-bootstrap';
import { ManualSubscriptionController } from '../../../../core/config/controllers/ManualSubscriptionController';
import { FeeController } from '../../../../core/config/controllers/FeeController';

export default function ManualSubscriptionModal({ show, onHide, userId, userSubscriptions, onSubscriptionUpdated }) {
    const [activeTab, setActiveTab] = useState('create');
    const [plans, setPlans] = useState([]);
    const [prices, setPrices] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Create subscription form state
    const [createForm, setCreateForm] = useState({
        planId: '',
        priceId: '',
        durationMonths: 1,
        paymentAmount: '',
        paymentMethod: 'pix',
        paymentReference: '',
        notes: ''
    });

    // Extend subscription form state
    const [extendForm, setExtendForm] = useState({
        extensionMonths: 1,
        paymentAmount: '',
        paymentReference: '',
        notes: ''
    });

    // Cancel subscription form state
    const [cancelForm, setCancelForm] = useState({
        cancelImmediately: false,
        notes: ''
    });

    useEffect(() => {
        if (show) {
            loadPlans();
        }
    }, [show]);

    const loadPlans = () => {
        FeeController.ListActivePlans((plansList) => {
            setPlans(plansList);
        });
    };

    const loadPricesForPlan = (planId) => {
        if (!planId) {
            setPrices([]);
            return;
        }
        FeeController.GetPlanPrices(planId, (pricesList) => {
            setPrices(pricesList);
        });
    };

    const hasManualSubscription = () => {
        return userSubscriptions && userSubscriptions.some(sub => 
            sub.status === 'active' && 
            (sub.subscription_type === 'manual' || !sub.subscription_type)
        );
    };

    const hasStripeSubscription = () => {
        return userSubscriptions && userSubscriptions.some(sub => 
            sub.status === 'active' && sub.subscription_type === 'stripe'
        );
    };

    const canCreateManualSubscription = () => {
        return !hasStripeSubscription();
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        if (!canCreateManualSubscription()) {
            alert('Usuário já possui assinatura Stripe ativa. Não é possível criar assinatura manual.');
            return;
        }
        
        setLoading(true);
        try {
            await ManualSubscriptionController.CreateManualSubscription({
                userId: userId,
                planId: createForm.planId,
                priceId: createForm.priceId,
                durationMonths: parseInt(createForm.durationMonths),
                paymentAmount: parseFloat(createForm.paymentAmount),
                paymentMethod: createForm.paymentMethod,
                paymentReference: createForm.paymentReference,
                notes: createForm.notes
            }, (result) => {
                onSubscriptionUpdated();
                onHide();
                resetForms();
            });
        } catch (error) {
            console.error('Error creating manual subscription:', error);
        }
        setLoading(false);
    };

    const handleExtendSubmit = async (e) => {
        e.preventDefault();
        if (!hasManualSubscription()) {
            alert('Usuário não possui assinatura manual ativa para estender.');
            return;
        }
        
        setLoading(true);
        try {
            await ManualSubscriptionController.ExtendManualSubscription({
                userId: userId,
                extensionMonths: parseInt(extendForm.extensionMonths),
                paymentAmount: parseFloat(extendForm.paymentAmount),
                paymentReference: extendForm.paymentReference,
                notes: extendForm.notes
            }, (result) => {
                onSubscriptionUpdated();
                onHide();
                resetForms();
            });
        } catch (error) {
            console.error('Error extending manual subscription:', error);
        }
        setLoading(false);
    };

    const handleCancelSubmit = async (e) => {
        e.preventDefault();
        if (!hasManualSubscription()) {
            alert('Usuário não possui assinatura manual ativa para cancelar.');
            return;
        }
        
        setLoading(true);
        try {
            await ManualSubscriptionController.CancelManualSubscription({
                userId: userId,
                cancelImmediately: cancelForm.cancelImmediately,
                notes: cancelForm.notes
            }, (result) => {
                onSubscriptionUpdated();
                onHide();
                resetForms();
            });
        } catch (error) {
            console.error('Error canceling manual subscription:', error);
        }
        setLoading(false);
    };

    const resetForms = () => {
        setCreateForm({
            planId: '',
            priceId: '',
            durationMonths: 1,
            paymentAmount: '',
            paymentMethod: 'pix',
            paymentReference: '',
            notes: ''
        });
        setExtendForm({
            extensionMonths: 1,
            paymentAmount: '',
            paymentReference: '',
            notes: ''
        });
        setCancelForm({
            cancelImmediately: false,
            notes: ''
        });
        setPrices([]);
    };

    const paymentMethods = [
        { value: 'pix', label: 'PIX' },
        { value: 'bank_transfer', label: 'Transferência Bancária' },
        { value: 'cash', label: 'Dinheiro' },
        { value: 'other', label: 'Outro' }
    ];

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Gerenciar Assinatura Manual</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
                    <Nav.Item>
                        <Nav.Link 
                            eventKey="create" 
                            disabled={!canCreateManualSubscription()}
                            title={!canCreateManualSubscription() ? "Usuário já possui assinatura Stripe ativa" : ""}
                        >
                            Criar Assinatura
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link 
                            eventKey="extend"
                            disabled={!hasManualSubscription()}
                            title={!hasManualSubscription() ? "Usuário não possui assinatura manual ativa" : ""}
                        >
                            Estender Assinatura
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link 
                            eventKey="cancel"
                            disabled={!hasManualSubscription()}
                            title={!hasManualSubscription() ? "Usuário não possui assinatura manual ativa" : ""}
                        >
                            Cancelar Assinatura
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                {activeTab === 'create' && (
                    <Form onSubmit={handleCreateSubmit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Plano *</Form.Label>
                                    <Form.Select 
                                        value={createForm.planId} 
                                        onChange={(e) => {
                                            const newPlanId = e.target.value;
                                            setCreateForm({...createForm, planId: newPlanId, priceId: ''});
                                            loadPricesForPlan(newPlanId);
                                        }}
                                        required
                                    >
                                        <option value="">Selecione um plano</option>
                                        {plans.map(plan => (
                                            <option key={plan.id} value={plan.id}>
                                                {plan.name} - R$ {plan.price} ({plan.billing_cycle})
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Preço *</Form.Label>
                                    <Form.Select
                                        value={createForm.priceId}
                                        onChange={(e) => setCreateForm({...createForm, priceId: e.target.value})}
                                        required
                                        disabled={!createForm.planId || prices.length === 0}
                                    >
                                        <option value="">Selecione um preço</option>
                                        {prices.map(price => (
                                            <option key={price.id} value={price.id}>
                                                R$ {price.unit_amount ? (price.unit_amount / 100).toFixed(2) : price.amount} - {price.recurring?.interval || 'único'}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    {createForm.planId && prices.length === 0 && (
                                        <Form.Text className="text-muted">
                                            Carregando preços do plano...
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Duração (meses) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        value={createForm.durationMonths}
                                        onChange={(e) => setCreateForm({...createForm, durationMonths: e.target.value})}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Valor Pago (R$) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={createForm.paymentAmount}
                                        onChange={(e) => setCreateForm({...createForm, paymentAmount: e.target.value})}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Método de Pagamento *</Form.Label>
                                    <Form.Select 
                                        value={createForm.paymentMethod} 
                                        onChange={(e) => setCreateForm({...createForm, paymentMethod: e.target.value})}
                                        required
                                    >
                                        {paymentMethods.map(method => (
                                            <option key={method.value} value={method.value}>
                                                {method.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Referência do Pagamento</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={createForm.paymentReference}
                                        onChange={(e) => setCreateForm({...createForm, paymentReference: e.target.value})}
                                        placeholder="TXN789, PIX123, etc."
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Observações</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={createForm.notes}
                                onChange={(e) => setCreateForm({...createForm, notes: e.target.value})}
                                placeholder="Informações adicionais sobre o pagamento..."
                            />
                        </Form.Group>
                        <Button type="submit" variant="primary" disabled={loading}>
                            {loading ? 'Criando...' : 'Criar Assinatura Manual'}
                        </Button>
                    </Form>
                )}

                {activeTab === 'extend' && (
                    <Form onSubmit={handleExtendSubmit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Extensão (meses) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        value={extendForm.extensionMonths}
                                        onChange={(e) => setExtendForm({...extendForm, extensionMonths: e.target.value})}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Valor Pago (R$) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={extendForm.paymentAmount}
                                        onChange={(e) => setExtendForm({...extendForm, paymentAmount: e.target.value})}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Referência do Pagamento</Form.Label>
                            <Form.Control
                                type="text"
                                value={extendForm.paymentReference}
                                onChange={(e) => setExtendForm({...extendForm, paymentReference: e.target.value})}
                                placeholder="TXN789012, PIX456, etc."
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Observações</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={extendForm.notes}
                                onChange={(e) => setExtendForm({...extendForm, notes: e.target.value})}
                                placeholder="Informações adicionais sobre a extensão..."
                            />
                        </Form.Group>
                        <Button type="submit" variant="success" disabled={loading}>
                            {loading ? 'Estendendo...' : 'Estender Assinatura'}
                        </Button>
                    </Form>
                )}

                {activeTab === 'cancel' && (
                    <Form onSubmit={handleCancelSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="cancelImmediately"
                                label="Cancelar imediatamente (se desmarcado, cancela ao fim do período atual)"
                                checked={cancelForm.cancelImmediately}
                                onChange={(e) => setCancelForm({...cancelForm, cancelImmediately: e.target.checked})}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Motivo do Cancelamento *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={cancelForm.notes}
                                onChange={(e) => setCancelForm({...cancelForm, notes: e.target.value})}
                                placeholder="Descreva o motivo do cancelamento..."
                                required
                            />
                        </Form.Group>
                        <Button type="submit" variant="danger" disabled={loading}>
                            {loading ? 'Cancelando...' : 'Cancelar Assinatura'}
                        </Button>
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    );
}